import React, { useMemo } from "react"
import ImageIcon from "@mui/icons-material/Image"
import { Box } from "@mui/material"

// ─── Parse product name into drawable features ───────────────────
const parseFeatures = (name) => {
  if (!name) return {}
  const n = name.toUpperCase()

  // Drawer count: digit before S (B2S, B3S, B4S, B5S, H4I)
  let drawers = 0
  const sMatch = n.match(/(\d)[SI]/)
  if (sMatch) drawers = parseInt(sMatch[1])

  // Sink: BN prefix
  const isSink = /^BN/.test(n)

  // Oven: BFR, HFR, or -F suffix
  const isOven = /^[BH]F/.test(n) || /^HRR-F/.test(n)

  // Open/shelves: BOR, BOS, HOR, HOS, KNA, K-AN
  const isOpen = /^[BHL]O[RS]/.test(n) || /^KNA/.test(n)

  // Fridge: HRR, HWR
  const isFridge = /^H[RW]R/.test(n) && !isOven

  // Corner: BKM, BKS, KG, LG, HG
  const isCorner = /^BK/.test(n) || /^[KLH]G/.test(n)

  // Door cabinets: BS, BSE, BSR, BTR, BBM, BLX, most K/L/H prefixes
  // If no drawers and not a special type, it's a door cabinet
  const isDoor = drawers === 0 && !isSink && !isOven && !isOpen && !isFridge && !isCorner

  // GOLA suffix: -G or -*G
  const isGola = /G$/.test(n)

  return { drawers, isSink, isOven, isOpen, isFridge, isCorner, isDoor, isGola }
}

const BoxDiagram = ({
  length = 0, height = 0, width = 0,
  size = 64, showLabels = false,
  swatchUrl = null,
  productName = "",
}) => {
  const hasAny = length > 0 || height > 0 || width > 0
  const features = useMemo(() => parseFeatures(productName), [productName])

  if (!hasAny) {
    return (
      <Box sx={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ImageIcon sx={{ fontSize: size * 0.5, color: "#d1d5db" }} />
      </Box>
    )
  }

  const L = Math.max(length || 8, 8)
  const H = Math.max(height || 8, 8)
  const W = Math.max(width || 4, 4)

  const margin = size * 0.15
  const labelSpace = showLabels ? size * 0.12 : 0
  const drawArea = size - margin * 2 - labelSpace

  const maxDim = Math.max(L + W * 0.4, H + W * 0.35)
  const scale = drawArea / maxDim

  const pxL = L * scale
  const pxH = H * scale
  const dx = W * scale * 0.4
  const dy = W * scale * 0.35

  const ox = margin + labelSpace
  const oy = size - margin

  // Front face corners (L x H)
  const A = [ox, oy]
  const B = [ox + pxL, oy]
  const C = [ox + pxL, oy - pxH]
  const D = [ox, oy - pxH]

  // Top face
  const E = [ox + pxL + dx, oy - pxH - dy]
  const F = [ox + dx, oy - pxH - dy]

  // Right side
  const G = [ox + pxL + dx, oy - dy]

  const pts = (arr) => arr.map((p) => p.join(",")).join(" ")

  const frontFace = pts([A, B, C, D])
  const topFace = pts([D, C, E, F])
  const rightFace = pts([B, G, E, C])

  const fontSize = Math.max(size * 0.11, 7)

  // Front face inner bounds (with small inset)
  const inset = Math.max(pxL * 0.06, 1.5)
  const fX = A[0] + inset
  const fY = D[1] + inset
  const fW = pxL - inset * 2
  const fH = pxH - inset * 2

  // Unique ID for clip/pattern refs
  const uid = useMemo(() => `bd-${Math.random().toString(36).slice(2, 8)}`, [])

  // ─── Feature drawing helpers ────────────────────────────────
  const featureElements = []
  const handleR = Math.max(size * 0.025, 1.2)
  const lineColor = swatchUrl ? "rgba(255,255,255,0.45)" : "#94a3b8"
  const handleColor = swatchUrl ? "rgba(255,255,255,0.7)" : "#64748b"
  const dividerWidth = Math.max(size * 0.012, 0.5)

  if (features.drawers > 0) {
    // Horizontal drawer dividers with centered handle bars
    const count = features.drawers
    for (let i = 0; i < count; i++) {
      const y = fY + (fH / count) * i
      if (i > 0) {
        featureElements.push(
          <line key={`dr-${i}`} x1={fX} y1={y} x2={fX + fW} y2={y}
            stroke={lineColor} strokeWidth={dividerWidth} />
        )
      }
      // Handle bar in center of each drawer section
      const cy = y + fH / count / 2
      const barW = Math.min(fW * 0.3, 8)
      featureElements.push(
        <line key={`dh-${i}`}
          x1={fX + fW / 2 - barW / 2} y1={cy}
          x2={fX + fW / 2 + barW / 2} y2={cy}
          stroke={handleColor} strokeWidth={Math.max(dividerWidth * 1.5, 1)}
          strokeLinecap="round" />
      )
    }
  } else if (features.isSink) {
    // Sink basin: rounded rect in upper portion of front face
    const basinW = fW * 0.6
    const basinH = fH * 0.3
    const basinX = fX + (fW - basinW) / 2
    const basinY = fY + fH * 0.15
    const r = Math.min(basinW, basinH) * 0.2
    featureElements.push(
      <rect key="sink" x={basinX} y={basinY} width={basinW} height={basinH} rx={r} ry={r}
        fill="none" stroke={lineColor} strokeWidth={dividerWidth} />
    )
    // Faucet dot
    featureElements.push(
      <circle key="faucet" cx={fX + fW / 2} cy={basinY - basinH * 0.15}
        r={handleR} fill={handleColor} />
    )
  } else if (features.isOven) {
    // Oven window: rectangle in center
    const winW = fW * 0.7
    const winH = fH * 0.5
    const winX = fX + (fW - winW) / 2
    const winY = fY + fH * 0.15
    featureElements.push(
      <rect key="oven" x={winX} y={winY} width={winW} height={winH}
        rx={1} ry={1}
        fill={swatchUrl ? "rgba(0,0,0,0.15)" : "#cbd5e1"}
        stroke={lineColor} strokeWidth={dividerWidth} />
    )
    // Handle bar below window
    const barW = winW * 0.5
    const barY = winY + winH + fH * 0.12
    featureElements.push(
      <line key="oven-h" x1={fX + fW / 2 - barW / 2} y1={barY}
        x2={fX + fW / 2 + barW / 2} y2={barY}
        stroke={handleColor} strokeWidth={Math.max(dividerWidth * 1.5, 1)}
        strokeLinecap="round" />
    )
  } else if (features.isOpen) {
    // Open shelves: 2-3 horizontal shelf lines
    const shelves = 3
    for (let i = 1; i < shelves; i++) {
      const y = fY + (fH / shelves) * i
      featureElements.push(
        <line key={`sh-${i}`} x1={fX} y1={y} x2={fX + fW} y2={y}
          stroke={lineColor} strokeWidth={dividerWidth}
          strokeDasharray={`${size * 0.04},${size * 0.02}`} />
      )
    }
  } else if (features.isFridge) {
    // Fridge: two sections (freezer top, fridge bottom) with handle
    const splitY = fY + fH * 0.3
    featureElements.push(
      <line key="fr-div" x1={fX} y1={splitY} x2={fX + fW} y2={splitY}
        stroke={lineColor} strokeWidth={dividerWidth} />
    )
    // Vertical handle on right side
    const hx = fX + fW * 0.85
    featureElements.push(
      <line key="fr-h1" x1={hx} y1={splitY + fH * 0.08}
        x2={hx} y2={splitY + fH * 0.3}
        stroke={handleColor} strokeWidth={Math.max(dividerWidth * 1.5, 1)}
        strokeLinecap="round" />
    )
  } else if (features.isCorner) {
    // Corner: diagonal line across front face
    featureElements.push(
      <line key="corner" x1={fX} y1={fY + fH} x2={fX + fW} y2={fY}
        stroke={lineColor} strokeWidth={dividerWidth}
        strokeDasharray={`${size * 0.03},${size * 0.02}`} />
    )
    // Handle
    featureElements.push(
      <circle key="c-h" cx={fX + fW * 0.35} cy={fY + fH * 0.5}
        r={handleR} fill={handleColor} />
    )
  } else if (features.isDoor) {
    // Single or double door with handle circle
    // Check if it's likely a double door (wide cabinets)
    const isWide = length > 0 && length >= 800
    if (isWide) {
      // Double door: vertical center line + two handles
      const cx = fX + fW / 2
      featureElements.push(
        <line key="door-div" x1={cx} y1={fY} x2={cx} y2={fY + fH}
          stroke={lineColor} strokeWidth={dividerWidth} />
      )
      featureElements.push(
        <circle key="dh-l" cx={cx - fW * 0.08} cy={fY + fH * 0.45}
          r={handleR} fill={handleColor} />,
        <circle key="dh-r" cx={cx + fW * 0.08} cy={fY + fH * 0.45}
          r={handleR} fill={handleColor} />
      )
    } else {
      // Single door with handle
      featureElements.push(
        <circle key="dh" cx={fX + fW * 0.8} cy={fY + fH * 0.45}
          r={handleR} fill={handleColor} />
      )
    }
  }

  // GOLA indicator: thin line at top of front face
  if (features.isGola) {
    featureElements.push(
      <line key="gola" x1={fX} y1={fY + 1} x2={fX + fW} y2={fY + 1}
        stroke={swatchUrl ? "rgba(255,255,255,0.6)" : "#64748b"}
        strokeWidth={Math.max(dividerWidth * 2, 1.5)} />
    )
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
      <defs>
        {/* Clip path for front face (used for swatch image) */}
        <clipPath id={`${uid}-front`}>
          <polygon points={frontFace} />
        </clipPath>
        <clipPath id={`${uid}-top`}>
          <polygon points={topFace} />
        </clipPath>
        <clipPath id={`${uid}-right`}>
          <polygon points={rightFace} />
        </clipPath>
      </defs>

      {/* ─── Front face ─── */}
      {swatchUrl ? (
        <g clipPath={`url(#${uid}-front)`}>
          <image
            href={swatchUrl}
            x={A[0]} y={D[1]} width={pxL} height={pxH}
            preserveAspectRatio="xMidYMid slice"
          />
        </g>
      ) : (
        <polygon points={frontFace} fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1" />
      )}
      {/* Front face outline (always) */}
      <polygon points={frontFace} fill="none" stroke={swatchUrl ? "rgba(0,0,0,0.2)" : "#94a3b8"} strokeWidth="1" />

      {/* ─── Top face ─── */}
      {swatchUrl ? (
        <g clipPath={`url(#${uid}-top)`}>
          <image
            href={swatchUrl}
            x={D[0]} y={F[1]} width={pxL + dx} height={dy + 2}
            preserveAspectRatio="xMidYMid slice"
          />
          <polygon points={topFace} fill="rgba(255,255,255,0.25)" />
        </g>
      ) : (
        <polygon points={topFace} fill="#cbd5e1" stroke="#94a3b8" strokeWidth="1" />
      )}
      <polygon points={topFace} fill="none" stroke={swatchUrl ? "rgba(0,0,0,0.15)" : "#94a3b8"} strokeWidth="1" />

      {/* ─── Right face ─── */}
      {swatchUrl ? (
        <g clipPath={`url(#${uid}-right)`}>
          <image
            href={swatchUrl}
            x={B[0]} y={E[1]} width={dx + 2} height={pxH + dy}
            preserveAspectRatio="xMidYMid slice"
          />
          <polygon points={rightFace} fill="rgba(0,0,0,0.2)" />
        </g>
      ) : (
        <polygon points={rightFace} fill="#94a3b8" stroke="#64748b" strokeWidth="1" />
      )}
      <polygon points={rightFace} fill="none" stroke={swatchUrl ? "rgba(0,0,0,0.25)" : "#64748b"} strokeWidth="1" />

      {/* ─── Feature overlays on front face ─── */}
      {featureElements}

      {/* ─── Dimension labels ─── */}
      {showLabels && size >= 80 && (
        <>
          {length > 0 && (
            <text
              x={(A[0] + B[0]) / 2}
              y={A[1] + fontSize + 2}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#475569"
              fontFamily="sans-serif"
              fontWeight="600"
            >
              {length}
            </text>
          )}
          {height > 0 && (
            <text
              x={A[0] - 4}
              y={(A[1] + D[1]) / 2}
              textAnchor="end"
              fontSize={fontSize}
              fill="#475569"
              fontFamily="sans-serif"
              fontWeight="600"
              dominantBaseline="middle"
            >
              {height}
            </text>
          )}
          {width > 0 && (
            <text
              x={(C[0] + E[0]) / 2 + 2}
              y={(C[1] + E[1]) / 2 - 3}
              textAnchor="middle"
              fontSize={fontSize * 0.9}
              fill="#475569"
              fontFamily="sans-serif"
              fontWeight="600"
            >
              {width}
            </text>
          )}
        </>
      )}
    </svg>
  )
}

export default BoxDiagram
